import { Link } from 'react-router-dom';

/**
 * Getting Started Guide for Studio Admins
 * Step-by-step walkthrough of studio management in MediaRite
 */
export default function StudioGuide() {
  return (
    <div className="page guide-page">
      <div className="page-header">
        <h1>Studio Admin Getting Started Guide</h1>
        <p className="page-subtitle">
          Welcome to MediaRite! This guide will help you set up your studio and manage compliance.
        </p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>1. Your Dashboard</h2>
          <p>
            Your <Link to="/dashboard">Dashboard</Link> provides an overview of your studio's
            compliance status:
          </p>
          <ul>
            <li><strong>Compliance Rate</strong> - Percentage of productions with complete records</li>
            <li><strong>Total Documents</strong> - All documents in your system</li>
            <li><strong>Pending Signatures</strong> - Documents waiting for model signatures</li>
            <li><strong>Active Models</strong> - Models connected to your studio</li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>2. Managing Models</h2>
          <p>
            The <Link to="/models">Models</Link> page is where you manage your talent roster.
          </p>

          <h3>Adding a New Model</h3>
          <ol>
            <li>Go to <Link to="/models">Models</Link> page</li>
            <li>Click <strong>Add Model</strong></li>
            <li>Enter the model's email address</li>
            <li>If they already have a MediaRite account, a connection request is sent</li>
            <li>If not, an invitation email is sent to create an account</li>
          </ol>

          <h3>Model Connection Status</h3>
          <ul>
            <li><strong>Pending</strong> - Waiting for model to approve connection</li>
            <li><strong>Connected</strong> - Model approved, you can add them to productions</li>
            <li><strong>Rejected</strong> - Model declined the connection</li>
          </ul>

          <div className="guide-note">
            <strong>Note:</strong> Models control their own profiles and ID documents.
            You can view their information once connected, but cannot edit it.
          </div>
        </section>

        <section className="guide-section">
          <h2>3. Creating Productions</h2>
          <p>
            Productions are individual shoots or content pieces that require compliance records.
          </p>

          <h3>Creating a New Production</h3>
          <ol>
            <li>Go to <Link to="/productions">Productions</Link> page</li>
            <li>Click <Link to="/productions/new" className="btn-link">New Production</Link></li>
            <li>Enter production details:
              <ul>
                <li><strong>Title</strong> - Name of the production</li>
                <li><strong>Production Date</strong> - When the content is/was created</li>
                <li><strong>Description</strong> - Optional notes</li>
              </ul>
            </li>
            <li>Add models who are performing in this production</li>
            <li>Save the production</li>
          </ol>

          <h3>Adding Models to a Production</h3>
          <ol>
            <li>Open the production detail page</li>
            <li>Click <strong>Add Performer</strong></li>
            <li>Select from your connected models</li>
            <li>Required documents are automatically generated</li>
          </ol>
        </section>

        <section className="guide-section">
          <h2>4. Managing Documents</h2>
          <p>
            The <Link to="/documents">Documents</Link> page shows all compliance documents.
          </p>

          <h3>Document Types</h3>
          <ul>
            <li><strong>Model Release</strong> - Permission to use model's likeness</li>
            <li><strong>2257 Record</strong> - Federal age verification compliance record</li>
            <li><strong>Rights Agreement</strong> - Content usage rights (optional)</li>
          </ul>

          <h3>Document Status</h3>
          <ul>
            <li><strong>Draft</strong> - Document created, not yet sent for signing</li>
            <li><strong>Pending</strong> - Waiting for model signature</li>
            <li><strong>Signed</strong> - Fully executed and compliant</li>
            <li><strong>Expired</strong> - Document expired (if applicable)</li>
          </ul>

          <h3>Creating Documents Manually</h3>
          <ol>
            <li>Click <Link to="/documents/new">Create Document</Link></li>
            <li>Select the document type</li>
            <li>Choose the production (if applicable)</li>
            <li>Select the model</li>
            <li>Review and create</li>
          </ol>

          <div className="guide-note">
            <strong>Tip:</strong> Documents are usually created automatically when you add
            a model to a production. Manual creation is for special cases.
          </div>
        </section>

        <section className="guide-section">
          <h2>5. Compliance Tracking</h2>
          <p>
            The <Link to="/compliance">Compliance</Link> page shows your 2257 compliance status.
          </p>

          <h3>Understanding Compliance</h3>
          <ul>
            <li><strong>Compliant</strong> - All required documents signed, ID verified</li>
            <li><strong>Incomplete</strong> - Missing signatures or documents</li>
          </ul>

          <h3>Maintaining Compliance</h3>
          <ol>
            <li>Ensure all models have uploaded valid ID</li>
            <li>Get all model releases signed before or on production date</li>
            <li>Keep records organized by production</li>
            <li>Download PDFs for offline backup</li>
          </ol>

          <div className="guide-warning">
            <strong>Important:</strong> 18 U.S.C. § 2257 requires you to maintain records
            for 5 years after content creation. MediaRite handles this, but you should
            also maintain your own backups.
          </div>
        </section>

        <section className="guide-section">
          <h2>6. Managing Users</h2>
          <p>
            The <Link to="/users">Users</Link> page lets you manage who has access to
            your studio account.
          </p>

          <h3>User Roles</h3>
          <ul>
            <li><strong>Studio Admin</strong> - Full access to all features</li>
            <li><strong>Studio Staff</strong> - Limited access (view only, cannot delete)</li>
          </ul>

          <h3>Adding Staff Members</h3>
          <ol>
            <li>Go to <Link to="/users">Users</Link></li>
            <li>Click <strong>Invite User</strong></li>
            <li>Enter their email and select their role</li>
            <li>They'll receive an invitation email</li>
          </ol>
        </section>

        <section className="guide-section">
          <h2>7. Settings</h2>
          <p>
            The <Link to="/settings">Settings</Link> page lets you configure your studio:
          </p>
          <ul>
            <li><strong>Studio Name</strong> - Your business name</li>
            <li><strong>Custodian Information</strong> - Required for 2257 records</li>
            <li><strong>Contact Details</strong> - Business address and contact info</li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Common Workflows</h2>

          <h3>Complete Production Workflow</h3>
          <ol>
            <li><strong>Connect with Models</strong> - Add models and wait for approval</li>
            <li><strong>Create Production</strong> - Set up the production details</li>
            <li><strong>Add Performers</strong> - Add connected models to the production</li>
            <li><strong>Documents Generated</strong> - System creates required documents</li>
            <li><strong>Models Sign</strong> - Models sign their documents</li>
            <li><strong>Production Complete</strong> - All records are compliant</li>
          </ol>
        </section>

        <section className="guide-section">
          <h2>Need Help?</h2>
          <p>
            For technical support or questions about compliance requirements,
            contact MediaRite support.
          </p>
        </section>

        <div className="guide-actions">
          <Link to="/dashboard" className="btn btn-primary">
            Go to Dashboard
          </Link>
          <Link to="/models" className="btn btn-secondary">
            Manage Models
          </Link>
          <Link to="/productions/new" className="btn btn-secondary">
            Create Production
          </Link>
        </div>
      </div>
    </div>
  );
}
